import { IcoEdit, IcoRemove } from '@/utils/icons'
import { selectProfile } from '@store/profile.reducer'
import { Dispatch, FC, SetStateAction, useState } from 'react'
import { useSelector } from 'react-redux'
import { Empty, Modal } from 'antd'
import cn from 'classnames'
import classes from './index.module.css'
import amclasses from '@pages/Admin/CRM/styles/ammodal.module.css'
import { useAddAlert } from '@/hooks/useAddAlert'
import { BodyModal } from '@common/ProfileCard/BodyModal/BodyModal'
import Preloader from '@UI/Preloader'
import { classApi } from '@/api'
import { LeadPayments } from '@/api/types/crm/crm.types'
import { formatPrice, getLatestActivePayment } from './utils'
import Button from '@UI/New/Button'
import ModalRemoveClass from '@common/ProfileCard/BodyEdit/ModalRemoveClass'
import ActionModal from '@pages/Admin/AllUsers/modals/ActionModal'

interface CourseClassListProps {
  userId: number
  loading: boolean
  paymentData: LeadPayments
  refetchPaymentData: () => void | Promise<void>
}

type ModalType = { type: string; data?: object } | null

const formatGroupTeacherLine = (
  directionOrderTariff: LeadPayments['directions'][number]['directionOrderTariff'],
  directionGroup: LeadPayments['directions'][number]['directionGroup'],
  directionGroupTeacher: LeadPayments['directions'][number]['directionGroupTeacher'],
  directionTeacher: LeadPayments['directions'][number]['directionTeacher'],
) => {
  if (directionOrderTariff?.any_seminarian) return 'Любой педагог'
  if (directionGroup != null) {
    const g = directionGroup?.name?.trim() || ''
    const t = directionGroupTeacher?.name ?? 'Не распределено'
    return g ? `${g} · ${t}` : t
  }
  return directionTeacher?.name ?? 'Не распределено'
}

const getClassDirectionUniqueKey = (
  direction: LeadPayments['directions'][number],
) =>
  [
    direction?.directionOrder?.class_id ?? '',
    direction?.direction?.id ?? direction?.directionOrder?.direction_id ?? '',
    direction?.directionGroup?.id ?? direction?.directionOrder?.group_id ?? '',
    direction?.directionOrderTariff?.id ??
      direction?.directionOrder?.tariff_id ??
      '',
  ].join(':')

const getUniqueClassDirections = (
  directions: LeadPayments['directions'] = [],
) => {
  const usedKeys = new Set<string>()

  return directions.filter((direction) => {
    const key = getClassDirectionUniqueKey(direction)

    if (usedKeys.has(key)) {
      return false
    }

    usedKeys.add(key)
    return true
  })
}

const CourseClassList = ({
  userId,
  loading,
  paymentData,
  refetchPaymentData,
}: CourseClassListProps) => {
  const addAlert = useAddAlert()

  const platformSettings = useSelector((store: any) => store.settings)
  const [modal, setModal] = useState<ModalType>(null)

  const userIdsAllowedToDeleteCourses =
    'userIdsAllowedToDeleteCourses' in platformSettings &&
    platformSettings.userIdsAllowedToDeleteCourses
      ? JSON.parse(platformSettings.userIdsAllowedToDeleteCourses as string)
      : []
  const selectEmployeesWhoAreAllowedDeleteCourses =
    'selectEmployeesWhoAreAllowedDeleteCourses' in platformSettings &&
    platformSettings.selectEmployeesWhoAreAllowedDeleteCourses === '1'

  const profile = useSelector(selectProfile)

  const canDeleteCourse =
    userIdsAllowedToDeleteCourses.includes(profile.id) ||
    !selectEmployeesWhoAreAllowedDeleteCourses

  return (
    <>
      {loading ? (
        <div className={classes.listLoading}>
          <Preloader />
        </div>
      ) : paymentData.classes.length < 1 &&
        paymentData.directions.length < 1 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Список покупок пока пуст"
          className={classes.empty}
        />
      ) : (
        <div className={cn(classes.mainList, 'custom-scroll')}>
          <DirectionList
            directions={paymentData.directions}
            setModal={setModal}
            canDeleteCourse={canDeleteCourse}
            addAlert={addAlert}
            refetchList={refetchPaymentData}
            userId={userId}
          />
          <ClassesList
            classes={paymentData.classes}
            setModal={setModal}
            canDeleteCourse={canDeleteCourse}
            addAlert={addAlert}
            refetchList={refetchPaymentData}
            userId={userId}
          />
        </div>
      )}

      <Modal
        open={modal !== null}
        onOk={() => setModal(null)}
        onCancel={() => setModal(null)}
        footer={null}
        title={null}
        centered
        maskClosable
        width={680}
        destroyOnClose
        className={cn(amclasses.modal)}
      >
        <BodyModal
          type={modal?.type || ''}
          data={modal?.data}
          state={{ id: userId }}
          setState={() => {}}
          close={() => setModal(null)}
          setBodyModal={modal}
          addAlert={addAlert}
          getDirection={() => refetchPaymentData()}
        />
      </Modal>
    </>
  )
}

interface OtherListProps {
  setModal: Dispatch<SetStateAction<ModalType>>
  canDeleteCourse: boolean
  addAlert: (data: any) => void
  refetchList: () => void | Promise<void>
  userId: number
}

const ClassesList: FC<
  {
    classes: LeadPayments['classes']
  } & OtherListProps
> = ({
  classes: classesData,
  setModal,
  addAlert,
  refetchList,
  userId,
  ...props
}) => {
  const [modalRemoveClass, setModalRemoveClass] = useState<{
    active: boolean
    data: { user_id?: number; elClass?: LeadPayments['classes'][number]['class'] }
  }>({
    active: false,
    data: {},
  })
  const [modalDetachGroup, setModalDetachGroup] = useState<{
    isOpen: boolean
    text?: string
    onConfirm?: () => void
    onDismiss?: () => void
  }>({
    isOpen: false,
  })

  return classesData?.map((paymentClass) => {
    const { class: classData, classUser, directions, payments } = paymentClass
    const classDirections = getUniqueClassDirections(directions)
    const payment = getLatestActivePayment(payments)
    const isPaid = Boolean(
      payment && (payment.status === 1 || payment.is_manual === 1),
    )
    const paidSum =
      Number(classUser?.amount || 0) + Number(classUser?.discount || 0)
    const periodText = !payment
      ? '—'
      : payment.buy_hours === null || payment.buy_hours === undefined
        ? payment.old_date && payment.new_date
          ? `${payment.old_date} — ${payment.new_date}`
          : (payment.old_date ?? '—')
        : `${payment.buy_hours} ч.`

    return (
      <div key={`c${paymentClass.class.id}`} className={classes.cardClass}>
        <div className={classes.cardClassHeader}>
          <div>
            <span className={classes.cardEyebrow}>Класс</span>
            <h3 className={classes.cardClassTitle}>{classData.title}</h3>
            <span
              className={cn(
                classes.statusPill,
                isPaid ? classes.statusPillOk : classes.statusPillWarn,
              )}
            >
              {isPaid ? 'Оплачено' : 'В процессе оплаты'}
            </span>
          </div>
        </div>

        <div className={classes.priceRow}>
          <PurchaseCell
            label="Стоимость класса"
            value={`${formatPrice(Number(classData?.amount) ?? 0)} руб.`}
          />
          <PurchaseCell
            label={isPaid ? 'Оплачено по классу' : 'Сумма (прогресс)'}
            value={`${formatPrice(paidSum)} руб.`}
            accent
          />
        </div>

        <div className={classes.metaFlow}>
          {payment?.funnel?.funnel_name ? (
            <span
              className={classes.funnelPill}
              title={payment.funnel.funnel_name}
            >
              {payment.funnel.funnel_name}
            </span>
          ) : null}
          <span className={classes.payMeta}>
            {payment?.buy_hours === null || payment?.buy_hours === undefined
              ? 'Период оплаты'
              : 'Часы'}
            : {periodText}
          </span>
        </div>

        <div className={classes.toolbarRow}>
          <Button
            color="primary"
            variant="outline"
            className={classes.toolbarBtn}
            onClick={() =>
              setModal({
                type: 'AddGroupToClass',
                data: {
                  classId: classData.id,
                  selected: classDirections.map(
                    ({ directionOrder }) => directionOrder.group_id,
                  ),
                  onSuccess: () => refetchList(),
                  onError: ({ data }: any) =>
                    addAlert({
                      type: 'error',
                      title: 'Не удалось добавить группу',
                      text: Object.entries(data.data)[0][1] || '',
                    }),
                },
              })
            }
          >
            Добавить группу
          </Button>
          <Button
            color="danger"
            className={classes.toolbarBtn}
            onClick={() =>
              setModalRemoveClass({
                active: true,
                data: {
                  user_id: userId,
                  elClass: classData,
                },
              })
            }
          >
            Удалить класс
          </Button>
        </div>

        {classDirections.length ? (
          <div className={classes.nestedSection}>
            <div className={classes.nestedSectionLabel}>Направления в классе</div>
            <div className={classes.nestedList}>
              <DirectionList
                nested
                hiddenPaymentBlock
                hiddenEditButton
                detachDirection={(direction) =>
                  setModalDetachGroup({
                    isOpen: true,
                    text: `Удалить ученика из группы «${direction.directionGroup.name}»?`,
                    onConfirm: () => {
                      classApi
                        .detachUserClassGroup(classData.id, {
                          direction_group_id: direction.directionGroup.id,
                          user_id: userId,
                        })
                        .then(() => refetchList())
                      setModalDetachGroup({ isOpen: false })
                    },
                    onDismiss: () => setModalDetachGroup({ isOpen: false }),
                  })
                }
                directions={classDirections}
                setModal={setModal}
                addAlert={addAlert}
                refetchList={refetchList}
                userId={userId}
                {...props}
              />
            </div>
          </div>
        ) : null}

        <Modal
          open={modalRemoveClass.active}
          onOk={() => setModalRemoveClass({ active: false, data: {} })}
          onCancel={() => setModalRemoveClass({ active: false, data: {} })}
          footer={null}
          title={null}
          centered
          maskClosable
          className={cn(amclasses.modal)}
        >
          <ModalRemoveClass
            closeModal={() => setModalRemoveClass({ active: false, data: {} })}
            elClass={modalRemoveClass.data.elClass}
            user_id={modalRemoveClass.data.user_id}
            setClasses={() => {}}
            fetchClasses={() => refetchList()}
          />
        </Modal>
        <Modal
          open={modalDetachGroup?.isOpen}
          onOk={() => setModalDetachGroup({ isOpen: false })}
          onCancel={() => setModalDetachGroup({ isOpen: false })}
          footer={null}
          title={null}
          centered
          maskClosable
          className={cn(amclasses.modal, classes.modalFullContent)}
        >
          <div>
            <ActionModal
              title="Открепить от группы"
              content={modalDetachGroup?.text}
              onDismiss={modalDetachGroup?.onDismiss}
              onAction={modalDetachGroup?.onConfirm}
            />
          </div>
        </Modal>
      </div>
    )
  })
}

const DirectionList: FC<
  {
    directions: LeadPayments['directions']
    nested?: boolean
    hiddenPaymentBlock?: boolean
    hiddenEditButton?: boolean
    detachDirection?: (direction: LeadPayments['directions'][number]) => void
  } & OtherListProps
> = ({
  directions,
  nested,
  hiddenPaymentBlock,
  hiddenEditButton,
  detachDirection,
  refetchList: _refetchList,
  setModal,
  canDeleteCourse,
}) =>
  directions?.map((paymentDirection) => {
    const {
      direction,
      directionOrder,
      directionOrderTariff,
      directionTeacher,
      directionGroup,
      directionGroupTeacher,
      payments,
      privateInfo,
    } = paymentDirection
    const payment = getLatestActivePayment(payments)
    const isPaidDir = Boolean(
      payment && (payment.status === 1 || payment.is_manual === 1),
    )
    const groupTeacher = formatGroupTeacherLine(
      directionOrderTariff,
      directionGroup,
      directionGroupTeacher,
      directionTeacher,
    )

    const periodDir = !payment
      ? '—'
      : payment.buy_hours === null || payment.buy_hours === undefined
        ? payment.old_date && payment.new_date
          ? `${payment.old_date} — ${payment.new_date}`
          : (payment.old_date ?? '—')
        : `${payment.buy_hours} ч.`

    return (
      <div
        className={cn(classes.cardDirection, nested && classes.cardDirectionNested)}
        key={`d-${paymentDirection.direction.id}-${directionOrder?.id ?? 'o'}`}
      >
        <div className={classes.cardDirectionTop}>
          <div className={classes.cardDirectionTitleBlock}>
            {!nested ? (
              <span className={classes.cardEyebrow}>Направление</span>
            ) : null}
            <h4 className={classes.cardDirectionTitle}>
              {direction?.name ?? 'Без названия'}
            </h4>
          </div>
          <div className={classes.cardActions}>
            {!hiddenEditButton && privateInfo?.salary_per_hour ? (
              <button
                type="button"
                aria-label="Редактировать направление"
                className={classes.iconAction}
                onClick={() => {
                  setModal({
                    type: 'AddDirection',
                    data: {
                      item: {
                        ...directionOrder,
                        direction: {
                          ...direction,
                          directionTariffs: [directionOrderTariff],
                        },
                      },
                      privateInfo: privateInfo,
                      teacher: directionTeacher,
                    },
                  })
                }}
              >
                <IcoEdit />
              </button>
            ) : null}
            {canDeleteCourse ? (
              <button
                type="button"
                aria-label="Удалить направление"
                className={cn(classes.iconAction, classes.iconActionDanger)}
                onClick={() => {
                  if (!detachDirection) {
                    setModal({
                      type: 'RemoveDirection',
                      data: directionOrder.id as any,
                    })
                  } else {
                    detachDirection(paymentDirection)
                  }
                }}
              >
                <IcoRemove />
              </button>
            ) : null}
          </div>
        </div>

        <div className={classes.metaGrid2}>
          <PurchaseCell label="Тариф" value={directionOrderTariff?.name ?? '—'} />
          <PurchaseCell
            label="Группа · педагог"
            value={groupTeacher}
            multiline
          />
        </div>

        {privateInfo?.salary_per_hour ? (
          <div className={classes.subPanel}>
            <div className={classes.subPanelTitle}>Индивидуальные условия</div>
            <div className={classes.metaGrid4}>
              <PurchaseCell
                label="Занятий в неделю"
                value={privateInfo?.lessons_per_week?.toString() ?? '—'}
              />
              <PurchaseCell
                label="Длительность, ч"
                value={privateInfo?.time_one_lesson?.toString() ?? '—'}
              />
              <PurchaseCell
                label="Цена / ч"
                value={
                  privateInfo?.hour_cost
                    ? `${privateInfo.hour_cost} руб.`
                    : '—'
                }
              />
              <PurchaseCell
                label="Педагог / ч"
                value={
                  privateInfo?.salary_per_hour
                    ? `${privateInfo.salary_per_hour} руб.`
                    : '—'
                }
              />
            </div>
          </div>
        ) : null}

        {!hiddenPaymentBlock ? (
          <div className={classes.payStrip}>
            {payment?.funnel?.funnel_name ? (
              <span
                className={classes.funnelPill}
                title={payment.funnel.funnel_name}
              >
                {payment.funnel.funnel_name}
              </span>
            ) : null}
            <span
              className={cn(
                classes.statusPill,
                isPaidDir ? classes.statusPillOk : classes.statusPillWarn,
              )}
            >
              {isPaidDir ? 'Оплачено' : 'В оплате'}
            </span>
            <span className={classes.payAmount}>
              {formatPrice(payment?.amount ?? 0)} руб.
            </span>
            <span className={classes.payMeta}>
              {payment?.buy_hours === null || payment?.buy_hours === undefined
                ? 'Период'
                : 'Часы'}
              : {periodDir}
            </span>
          </div>
        ) : null}
      </div>
    )
  })

const PurchaseCell: FC<{
  label: string
  value: string
  accent?: boolean
  span2?: boolean
  /** Длинный текст (группа/педагог) без однострочного ellipsis */
  multiline?: boolean
}> = ({ label, value, accent, span2, multiline }) => (
  <div
    className={cn(
      classes.purchaseCell,
      accent && classes.purchaseCellAccent,
      span2 && classes.purchaseCellSpan2,
    )}
  >
    <span className={classes.purchaseCellLabel}>{label}</span>
    <span
      className={cn(
        classes.purchaseCellValue,
        multiline && classes.purchaseCellValueMultiline,
        !multiline && classes.truncateText,
      )}
    >
      {value}
    </span>
  </div>
)

export default CourseClassList
